/**
 * Router singleton — holds the createBrowserRouter instance after App.tsx creates it.
 *
 * Why this exists:
 *   axios.ts / AuthContext / MassagerAuthContext need to call router.navigate() for
 *   programmatic redirects (e.g. force-logout). They cannot statically import App.tsx
 *   because App.tsx → routes → pages → hooks → axios creates a circular dependency.
 *   The previous workaround was `void import('@/App')` (dynamic import), which Vite
 *   correctly warns about as "ineffective" since App.tsx is already in the main chunk.
 *
 *   This module imports nothing → no circular risk. App.tsx calls setRouter() once on
 *   startup. Callers use getRouter() — by the time any redirect fires the router is set.
 */

import type { Router } from '@remix-run/router';

let _router: Router | null = null;

export function setRouter(router: Router): void {
  _router = router;
}

export function getRouter(): Router {
  if (!_router) {
    throw new Error('[routerRef] Router accessed before App.tsx initialised it.');
  }
  return _router;
}
